import { useMemo } from "react";
import {
  MapPin,
  Pencil,
  Trash2,
  Plus,
  Users,
  Sparkles,
} from "lucide-react";
import { DEFAULT_TAG_COLOR } from "@/lib/folderStyle";

/**
 * ContactsListPanel — right-pane list view for the Dashboard.
 *
 * Renders the filtered contacts as compact cards. All filter/search logic
 * happens in the parent (DashboardPage) — this component just paints what
 * it's given and forwards user actions upward.
 */
function initialsOf(name) {
  if (!name) return "·";
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");
}

export default function ContactsListPanel({
  contacts, // already filtered by parent
  totalCount,
  tagById,
  contactColors,
  meetingBadgesById,
  upcomingContactIds,
  onSelect,
  onEdit,
  onDelete,
  onCreateNew,
}) {
  const sorted = useMemo(
    () => [...contacts].sort((a, b) => (a.name || "").localeCompare(b.name || "")),
    [contacts]
  );

  return (
    <div className="h-full flex flex-col" data-testid="contacts-panel">
      <div className="flex items-center justify-between gap-3 px-6 py-4 border-b border-white/10">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-white/45 font-mono flex items-center gap-1.5">
            <Users className="h-3 w-3 text-[#00e5ff]" /> Contacts
          </div>
          <div className="font-display text-2xl tracking-wide text-white">
            {contacts.length}
            <span className="text-white/40 text-sm font-mono ml-2">
              / {totalCount} shown
            </span>
          </div>
        </div>
        <button
          type="button"
          onClick={onCreateNew}
          className="btn-neon inline-flex items-center gap-2 h-11 px-4 rounded-2xl border border-[#00e5ff]/40 bg-[#00e5ff]/10 text-[#00e5ff] hover:bg-[#00e5ff]/20 font-display uppercase tracking-wider text-sm"
          data-testid="contacts-new-btn"
        >
          <Plus className="h-4 w-4" /> New contact
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        {sorted.length === 0 ? (
          <div
            className="flex flex-col items-center justify-center h-full text-center text-white/40 gap-3"
            data-testid="contacts-empty-state"
          >
            <Sparkles className="h-10 w-10 text-white/20" />
            <div className="font-display uppercase tracking-[0.2em] text-sm text-white/60">
              No contacts match
            </div>
            <div className="text-xs font-mono max-w-xs">
              Adjust the filters on the left, clear them, or tap “New contact” to add one.
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {sorted.map((c) => {
              const color = contactColors?.get(c.id) || DEFAULT_TAG_COLOR;
              const tagIds = (c.tag_ids || []).slice(0, 4);
              const badges = meetingBadgesById?.get(c.id) || [];
              const upcoming = upcomingContactIds?.has(c.id);
              return (
                <div
                  key={c.id}
                  className="dashboard-contact-card group"
                  data-testid={`contact-card-${c.id}`}
                >
                  <button
                    type="button"
                    onClick={() => onSelect?.(c)}
                    className="text-left flex-1 min-w-0"
                    data-testid={`contact-card-open-${c.id}`}
                  >
                    <div className="flex items-center gap-3">
                      {c.avatar ? (
                        <img
                          src={c.avatar}
                          alt=""
                          className="w-12 h-12 rounded-full object-cover shrink-0"
                          style={{
                            border: `2px solid ${color}`,
                            boxShadow: `0 0 10px ${color}AA`,
                          }}
                          draggable={false}
                        />
                      ) : (
                        <div
                          className="w-12 h-12 rounded-full flex items-center justify-center shrink-0 font-display text-lg"
                          style={{
                            background: "rgba(255,255,255,0.05)",
                            border: `2px solid ${color}`,
                            color,
                          }}
                        >
                          {initialsOf(c.name)}
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="font-display tracking-wide uppercase text-white text-[15px] leading-tight truncate">
                            {c.name || "Untitled"}
                          </span>
                          {upcoming && (
                            <span className="chip chip-upcoming">•</span>
                          )}
                        </div>
                        {(c.town || c.region) && (
                          <div className="text-[11px] text-white/45 font-mono truncate mt-0.5 flex items-center gap-1">
                            <MapPin className="h-2.5 w-2.5" />
                            {[c.town, c.region].filter(Boolean).join(" · ")}
                          </div>
                        )}
                      </div>
                    </div>

                    {(badges.length > 0 || tagIds.length > 0) && (
                      <div className="mt-2.5 flex flex-wrap gap-1">
                        {badges.map((b, i) => (
                          <span
                            key={`b-${i}`}
                            className="inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-mono uppercase tracking-widest"
                            style={{
                              color: b.color,
                              border: `1px solid ${b.color}88`,
                              background: `${b.color}18`,
                            }}
                          >
                            {b.name}
                          </span>
                        ))}
                        {tagIds.map((tid) => {
                          const t = tagById?.get(tid);
                          if (!t) return null;
                          const tc = t.color || DEFAULT_TAG_COLOR;
                          return (
                            <span
                              key={tid}
                              className="inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-mono uppercase tracking-widest"
                              style={{
                                background: `${tc}15`,
                                color: tc,
                                border: `1px solid ${tc}55`,
                              }}
                            >
                              {t.name}
                            </span>
                          );
                        })}
                        {(c.tag_ids || []).length > 4 && (
                          <span className="text-[9px] text-white/40 font-mono self-center">
                            +{(c.tag_ids || []).length - 4}
                          </span>
                        )}
                      </div>
                    )}
                  </button>

                  <div className="ml-2 flex items-start gap-1 opacity-70 group-hover:opacity-100 transition">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEdit?.(c);
                      }}
                      className="p-1.5 rounded-md text-white/50 hover:text-white hover:bg-white/5 transition"
                      aria-label={`Edit ${c.name}`}
                      data-testid={`contact-card-edit-${c.id}`}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete?.(c);
                      }}
                      className="p-1.5 rounded-md text-white/50 hover:text-red-400 hover:bg-red-500/10 transition"
                      aria-label={`Delete ${c.name}`}
                      data-testid={`contact-card-delete-${c.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
